import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getRevenueConfig, getPropertyRevenue } from '@/lib/api/api'
import { transformToLegacy } from '@/lib/api/bridge/transformers'
import { parsePeriodToLocalDateStrings } from '@/lib/utils/heatmapUtils'
import { toCents } from '@/lib/utils/revenueUtils'
import {
  logAttributionEvent,
  reportAttributionFailure,
} from '@/lib/observability/attribution'
import type { RevenueSetup } from '@/lib/types/workspace'
import type { ClassifiedTransactions } from '@/lib/utils/revenueUtils'
import { useMetaAds } from './useMetaAds'
import { useGoogleAds } from './useGoogleAds'
import { useRevenueClassification, type RevenueByDimension } from './useRevenueClassification'
import { useAdsAttribution } from './useAdsAttribution'
import {
  buildSourceRevenueRows,
  distinctCustomers,
  topTrialSource,
  type SourceRevenueRow,
} from '../attributionView'

// Only flag as paid when utm_medium explicitly signals paid traffic
const isPaidMedium = (medium: string): boolean => {
  const m = (medium || '').toLowerCase()
  return m.includes('cpc') || m.includes('ppc') || m.includes('paid') || m.includes('paidsocial')
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SourceRow {
  source: string; sessions: number; isPaid: boolean; revenueCents: number
}

interface CampaignRow {
  campaign: string; sessions: number; revenueCents: number
}

export interface LandingPageRow {
  page: string; visits: number; revenueCents: number
}

export interface ChartDataPoint {
  date: string; rawDate: string; spend: number; clicks: number
  sessions: number; revenue: number; attributedRevenue: number
  /** Payments joined to an ad campaign (same rule as the Campaigns table) */
  adRevenue: number
  /** Revenue matched to a visit but NOT from an ad campaign (organic/direct/referral) */
  otherRevenue: number
  trials: number
}

export interface HumblyticsTraffic {
  paidSessions: number
  totalSessions: number
  sourceData: SourceRow[]
  campaignData: CampaignRow[]
}

export interface AttributionData {
  // Connection status
  metaConnections: import('@/lib/api/oauth').MetaConnection[]
  googleAdsConnections: import('@/lib/api/oauth').GoogleAdsConnection[]
  isRevenueConfigured: boolean
  connectorType: 'stripe' | 'foxy' | null
  isLoadingRevenue: boolean

  // Aggregated metrics
  /** Spend is the server's ads-attribution total once it loads (the figure the
   *  Campaigns table and Return per $1 use); clicks and impressions are the
   *  platforms' own totals. */
  adTotals: { spend: number; impressions: number; clicks: number; conversions: number }
  /** Spend per platform, to name the one platform carrying it ("Meta ads") */
  adSpendByPlatform: { meta: number; google: number }
  totalRevenueDollars: number
  attributedRevenueDollars: number
  /** Revenue from payments joined to an ad campaign; the only numerator for ROAS */
  adRevenueDollars: number
  /** Paying customers from ad campaigns (payments when the server count is missing) */
  adPaymentCount: number
  trueROAS: number
  totalTrialCount: number

  // Processed data
  revenueAll: any[]
  classified: ClassifiedTransactions
  revenueDims: RevenueByDimension
  humblyticsData: HumblyticsTraffic
  landingPagesWithRevenue: LandingPageRow[]
  chartData: ChartDataPoint[]
  revenueMetrics: any

  // Loading/error
  isLoadingMeta: boolean
  isLoadingGoogleAds: boolean
  isLoadingRevenueMetrics: boolean
  metaError: string | null

  // Revenue by source (Stripe only view)
  dateRange: { since: string; until: string } | null
  /** Payments in range, matched to a visit or not */
  paymentCount: number
  /** Distinct customers when payments carry an id, else null */
  customerCount: number | null
  /** Trials started in range, every source */
  trialCountAll: number
  sourceRevenueRows: SourceRevenueRow[]
  topTrialSource: { name: string; count: number } | null

  // Derived booleans
  hasAdData: boolean
  hasRevenue: boolean

  // Ads attribution server data (for UTM health diagnostics)
  adsAttributionData: import('./useAdsAttribution').AdsAttributionData | null
  isLoadingAdsAttribution: boolean
  /** The campaign join failed: Earned falls back to paid utm_medium and the Campaigns table can't render */
  adsAttributionError: string | null
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAttributionData(
  propertyId: string | undefined,
  period: string,
  dashboardData: any,
  isLoading: boolean,
): AttributionData {
  const { token } = useAuth()

  // The processing memos below run during render, where reporting to Sentry
  // would be a side effect. Failures are queued and flushed after commit.
  const queuedFailures = useRef<unknown[]>([])
  useEffect(() => {
    if (queuedFailures.current.length === 0) return
    const failures = queuedFailures.current
    queuedFailures.current = []
    failures.forEach((error) =>
      reportAttributionFailure('processing', error, { propertyId }, { period }),
    )
  })

  const dateRange = useMemo(
    () => parsePeriodToLocalDateStrings(period),
    [period],
  )

  // ── Revenue config ────────────────────────────────────────────────────
  const [revenueConfig, setRevenueConfig] = useState<RevenueSetup | null>(null)
  const [isLoadingRevenue, setIsLoadingRevenue] = useState(true)
  const [isLoadingRevenueMetrics, setIsLoadingRevenueMetrics] = useState(true)
  const [revenueMetrics, setRevenueMetrics] = useState<any>(null)

  useEffect(() => {
    const load = async () => {
      if (!propertyId || !token) { setIsLoadingRevenue(false); return }
      setIsLoadingRevenue(true)
      try {
        const response = await getRevenueConfig(propertyId, token)
        const setup = response?.revenueSetup
          ?? ((response?.stripe !== undefined || response?.foxy !== undefined) ? response : null)
        setRevenueConfig(setup)
      } catch (error) {
        reportAttributionFailure('revenue_config', error, { propertyId })
        setRevenueConfig(null)
      }
      finally { setIsLoadingRevenue(false) }
    }
    load()
  }, [propertyId, token])

  useEffect(() => {
    const load = async () => {
      if (!propertyId || !token) { setIsLoadingRevenueMetrics(false); return }
      setIsLoadingRevenueMetrics(true)
      try {
        // Revenue only: the live Stripe subscription summary paged every
        // subscription before the page could settle, and nothing here renders it.
        const revenueRes = await getPropertyRevenue(transformToLegacy.period(period), propertyId, token)
        setRevenueMetrics(revenueRes)
      } catch (error) {
        reportAttributionFailure('revenue', error, { propertyId }, { period })
      }
      finally { setIsLoadingRevenueMetrics(false) }
    }
    load()
  }, [propertyId, token, period])

  const isStripeConfigured = revenueConfig?.stripe?.configured === true
  const isFoxyConfigured = revenueConfig?.foxy?.configured === true
  const isRevenueConfigured = isStripeConfigured || isFoxyConfigured
  const connectorType = isStripeConfigured ? 'stripe' : isFoxyConfigured ? 'foxy' : null

  // ── Meta Ads ──────────────────────────────────────────────────────────
  const {
    metaConnections, metaCampaignsData, dailyInsights,
    isLoadingMeta, metaError,
  } = useMetaAds(propertyId ?? undefined, token ?? undefined, dateRange)

  // ── Google Ads ────────────────────────────────────────────────────────
  const {
    googleAdsConnections, googleAdsCampaignsData, googleAdsDailyInsights,
    isLoadingGoogleAds,
  } = useGoogleAds(propertyId ?? undefined, token ?? undefined, dateRange)

  // ── Server-side ads attribution (ROAS, trials, cost per trial) ────────
  const {
    data: adsAttributionData,
    isLoading: isLoadingAdsAttribution,
    error: adsAttributionError,
  } = useAdsAttribution(propertyId ?? undefined, dateRange?.since, dateRange?.until)

  // ── Revenue classification ────────────────────────────────────────────
  const {
    revenueAll, classified, revenueDims,
    totalRevenueDollars, attributedRevenueDollars,
  } = useRevenueClassification(dashboardData, revenueMetrics, { propertyId, period })

  // ── Humblytics traffic (UTM-based) ────────────────────────────────────
  const emptyHumblytics: HumblyticsTraffic = { paidSessions: 0, totalSessions: 0, sourceData: [], campaignData: [] }
  const humblyticsData = useMemo<HumblyticsTraffic>(() => {
    try {
      if (!dashboardData?.metrics) return emptyHumblytics

      const sourceStats: Record<string, { sessions: number; isPaid: boolean }> = {}
      const campaignStats: Record<string, number> = {}
      let paidSessions = 0
      let totalSessions = 0

      const utmResults = Array.isArray(dashboardData.metrics.utmResults) ? dashboardData.metrics.utmResults : []
      utmResults.forEach((data: any) => {
        const count = parseInt(data?.count) || 0
        if (data?.utm_source_aggregate === 'utm_source' && data?.utm_source) {
          const isPaid = isPaidMedium(data.utm_medium || '')
          if (!sourceStats[data.utm_source]) sourceStats[data.utm_source] = { sessions: 0, isPaid }
          sourceStats[data.utm_source].sessions += count
          if (isPaid) paidSessions += count
          totalSessions += count
        }
        if (data?.utm_campaign_aggregate === 'utm_campaign' && data?.utm_campaign) {
          campaignStats[data.utm_campaign] = (campaignStats[data.utm_campaign] || 0) + count
        }
      })

      return {
        paidSessions,
        totalSessions: dashboardData.metrics.trends?.totalSessions?.[0]?.total_sessions || totalSessions,
        sourceData: Object.entries(sourceStats)
          .map(([source, d]) => ({ source, ...d, revenueCents: revenueDims.bySource.get(source.toLowerCase().trim()) ?? 0 }))
          .sort((a, b) => b.sessions - a.sessions),
        campaignData: Object.entries(campaignStats)
          .map(([campaign, sessions]) => ({ campaign, sessions, revenueCents: revenueDims.byCampaign.get(campaign.toLowerCase().trim()) ?? 0 }))
          .sort((a, b) => b.sessions - a.sessions),
      }
    } catch (err) {
      queuedFailures.current.push(err)
      return emptyHumblytics
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData, revenueDims])

  // ── Ad platform aggregation ───────────────────────────────────────────
  const adSpendByPlatform = useMemo(() => {
    const sum = (rows: unknown) =>
      (Array.isArray(rows) ? rows : []).reduce(
        (total: number, acct: any) =>
          total + (acct?.campaigns || []).reduce((t: number, c: any) => t + (Number(c?.spend) || 0), 0),
        0,
      )
    return { meta: sum(metaCampaignsData), google: sum(googleAdsCampaignsData) }
  }, [metaCampaignsData, googleAdsCampaignsData])

  const adTotals = useMemo(() => {
    try {
      let spend = 0, impressions = 0, clicks = 0, conversions = 0
      const allAccountData = [
        ...(Array.isArray(metaCampaignsData) ? metaCampaignsData : []),
        ...(Array.isArray(googleAdsCampaignsData) ? googleAdsCampaignsData : []),
      ]
      allAccountData.forEach((acctData) => {
        (acctData?.campaigns || []).forEach((c: any) => {
          spend += Number(c?.spend) || 0
          impressions += Number(c?.impressions) || 0
          clicks += Number(c?.clicks) || 0
          conversions += Number(c?.conversions) || 0
        })
      })
      // One spend figure for the whole page: the Campaigns table and Return
      // per $1 come from the server, so the headline and tile use it too.
      const serverSpend = Number(adsAttributionData?.totals?.spend)
      if (Number.isFinite(serverSpend) && adsAttributionData?.totals?.spend != null) spend = serverSpend
      return { spend, impressions, clicks, conversions }
    } catch {
      return { spend: 0, impressions: 0, clicks: 0, conversions: 0 }
    }
  }, [metaCampaignsData, googleAdsCampaignsData, adsAttributionData])

  // Revenue an ad click actually earned. `attributedRevenueDollars` is every
  // payment matched to *any* visit (organic, direct, referral), so dividing it
  // by ad spend reads organic revenue as ad return. The server joins payments
  // to ad campaigns on utm_campaign (the Campaigns table); the client fallback
  // is the same join, then paid utm_medium when no server data came back.
  const adCampaignKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const c of adsAttributionData?.campaigns ?? []) {
      const k = String(c?.utm_campaign ?? '').toLowerCase().trim()
      if (k) keys.add(k)
    }
    return keys
  }, [adsAttributionData])
  const isAdPayment = useCallback(
    (tx: { utm_campaign?: unknown; utm_medium?: unknown }) => {
      const campaign = String(tx?.utm_campaign ?? '').toLowerCase().trim()
      if (adCampaignKeys.size > 0) return campaign !== '' && adCampaignKeys.has(campaign)
      return isPaidMedium(String(tx?.utm_medium ?? ''))
    },
    [adCampaignKeys],
  )
  const adRevenueDollars = useMemo(() => {
    const server = adsAttributionData?.totals?.revenue
    if (server != null && Number.isFinite(Number(server))) return Number(server)
    return classified.attributed.filter(isAdPayment).reduce((sum, tx) => sum + toCents(tx), 0) / 100
  }, [adsAttributionData, classified, isAdPayment])
  // Customers, not payments: a renewal is a second payment from the same one.
  const adPaymentCount = adsAttributionData?.totals?.revenue_customers
    ?? adsAttributionData?.totals?.revenue_conversions
    ?? classified.attributed.filter(isAdPayment).length

  const trueROAS = adsAttributionData?.totals?.roas
    ?? (adTotals.spend > 0 ? adRevenueDollars / adTotals.spend : 0)
  const totalTrialCount = adsAttributionData?.totals?.trial_count ?? 0

  // ── Daily chart data ──────────────────────────────────────────────────
  const chartData = useMemo<ChartDataPoint[]>(() => {
    try {
      if (!dateRange) return []
      const dayMap = new Map<string, { date: string; spend: number; clicks: number; sessions: number; revenueCents: number; attributedCents: number; adCents: number; trials: number }>()

      const allDailyRows = [
        ...(Array.isArray(dailyInsights) ? dailyInsights : []),
        ...(Array.isArray(googleAdsDailyInsights) ? googleAdsDailyInsights : []),
      ]
      allDailyRows.forEach((row) => {
        if (!row?.date) return
        const d = row.date
        const existing = dayMap.get(d) || { date: d, spend: 0, clicks: 0, sessions: 0, revenueCents: 0, attributedCents: 0, adCents: 0, trials: 0 }
        existing.spend += Number(row.spend) || 0
        existing.clicks += Number(row.clicks) || 0
        dayMap.set(d, existing)
      })

      ;(Array.isArray(revenueAll) ? revenueAll : []).forEach((row: any) => {
        const d = (row?.bucket || row?.time_bucket || row?.granularity || row?.date || '').slice(0, 10)
        if (!d) return
        const cents = Math.round(Number(row?.total_revenue ?? 0))
        const existing = dayMap.get(d) || { date: d, spend: 0, clicks: 0, sessions: 0, revenueCents: 0, attributedCents: 0, adCents: 0, trials: 0 }
        existing.revenueCents += cents
        dayMap.set(d, existing)
      })

      // revenueMetrics.revenueAttribution has rows with view_id/session context
      // granularity is the time bucket; total_revenue is in cents
      const attributionRows: any[] = revenueMetrics?.metrics?.trends?.revenueAttribution ?? dashboardData?.metrics?.trends?.revenueAttribution ?? []
      ;(Array.isArray(attributionRows) ? attributionRows : []).forEach((tx: any) => {
        if (!tx?.view_id && !tx?.session_id) return  // skip unattributed rows
        const d = (tx?.granularity || tx?.created_at || tx?.date || '').slice(0, 10)
        if (!d) return
        const cents = Number(tx?.total_revenue ?? tx?.amount_cents ?? tx?.total_amount_cents ?? 0)
        if (!cents) return
        const existing = dayMap.get(d) || { date: d, spend: 0, clicks: 0, sessions: 0, revenueCents: 0, attributedCents: 0, adCents: 0, trials: 0 }
        existing.attributedCents += cents
        if (isAdPayment(tx)) existing.adCents += cents
        dayMap.set(d, existing)
      })

      const sessionTrends = dashboardData?.metrics?.trends?.sessions ?? []
      ;(Array.isArray(sessionTrends) ? sessionTrends : []).forEach((row: any) => {
        const d = (row?.bucket || row?.time_bucket || row?.granularity || row?.date || '').slice(0, 10)
        if (!d) return
        const count = Number(row?.total_sessions ?? row?.count ?? row?.visitors ?? 0)
        const existing = dayMap.get(d) || { date: d, spend: 0, clicks: 0, sessions: 0, revenueCents: 0, attributedCents: 0, adCents: 0, trials: 0 }
        existing.sessions += count
        dayMap.set(d, existing)
      })

      const freeTrialRows: any[] = revenueMetrics?.metrics?.trends?.freeTrialAttribution ?? dashboardData?.metrics?.trends?.freeTrialAttribution ?? []
      ;(Array.isArray(freeTrialRows) ? freeTrialRows : []).forEach((row: any) => {
        const d = (row?.granularity || row?.date || '').slice(0, 10)
        if (!d) return
        const existing = dayMap.get(d) || { date: d, spend: 0, clicks: 0, sessions: 0, revenueCents: 0, attributedCents: 0, adCents: 0, trials: 0 }
        existing.trials += 1
        dayMap.set(d, existing)
      })

      return Array.from(dayMap.values())
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((d) => ({
          date: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          rawDate: d.date,
          spend: Math.round(d.spend * 100) / 100,
          clicks: d.clicks,
          sessions: d.sessions,
          revenue: Math.round(d.revenueCents) / 100,
          attributedRevenue: Math.round(d.attributedCents) / 100,
          adRevenue: Math.round(d.adCents) / 100,
          // 'Other revenue' = total Stripe collected minus ad-attributed, like the Analytics page.
          // This includes unmatched renewals and organic/direct payments.
          otherRevenue: Math.max(0, Math.round(d.revenueCents - d.adCents) / 100),
          trials: d.trials,
        }))
    } catch (err) {
      queuedFailures.current.push(err)
      return []
    }
  }, [dailyInsights, googleAdsDailyInsights, revenueAll, dashboardData, revenueMetrics, dateRange, isAdPayment])

  // ── Revenue by source (Stripe only) ───────────────────────────────────
  const freeTrialRows = useMemo<any[]>(() => {
    const rows = revenueMetrics?.metrics?.trends?.freeTrialAttribution ?? dashboardData?.metrics?.trends?.freeTrialAttribution
    return Array.isArray(rows) ? rows : []
  }, [revenueMetrics, dashboardData])

  const sourceRevenueRows = useMemo<SourceRevenueRow[]>(() => {
    try {
      // Pure referrer rows only: the bridge also files UTM rows under referer_group
      const aggregate = Array.isArray(dashboardData?.metrics?.aggregate) ? dashboardData.metrics.aggregate : []
      const referrers = aggregate
        .filter((r: any) => r?.referer_group_aggregate && !r?.utm_medium && !r?.utm_source)
        .map((r: any) => ({ referer_group: r.referer_group ?? null, count: Number(r.count) || 0 }))
      return buildSourceRevenueRows({
        payments: classified.attributed,
        utmSources: humblyticsData.sourceData,
        referrers,
      })
    } catch (err) {
      queuedFailures.current.push(err)
      return []
    }
  }, [dashboardData, classified, humblyticsData])

  const paymentCount = classified.attributed.length + classified.unattributed.length
  const customerCount = useMemo(
    () => distinctCustomers(classified.attributed.concat(classified.unattributed)),
    [classified],
  )
  const trialSource = useMemo(() => topTrialSource(freeTrialRows), [freeTrialRows])

  // ── Landing pages ─────────────────────────────────────────────────────
  const landingPagesWithRevenue = useMemo<LandingPageRow[]>(() => {
    try {
      const raw = dashboardData?.metrics?.entryPages
      const entryPages = (Array.isArray(raw) ? raw : []) as Array<{ page: string; entry_count: string }>
      return entryPages
        .filter((p) => p?.page != null)
        .map((p) => ({
          page: p.page,
          visits: parseInt(p.entry_count) || 0,
          revenueCents: revenueDims.byLandingPage.get(p.page?.toLowerCase().trim()) ?? 0,
        }))
        .sort((a, b) => b.revenueCents - a.revenueCents || b.visits - a.visits)
        .slice(0, 15)
    } catch (err) {
      queuedFailures.current.push(err)
      return []
    }
  }, [dashboardData, revenueDims])

  // ── Derived booleans ──────────────────────────────────────────────────
  const hasAdData = metaConnections.length > 0 || googleAdsConnections.length > 0 || adTotals.spend > 0
  const hasRevenue = totalRevenueDollars > 0

  const adCampaignCount = [
    ...(Array.isArray(metaCampaignsData) ? metaCampaignsData : []),
    ...(Array.isArray(googleAdsCampaignsData) ? googleAdsCampaignsData : []),
  ].reduce((n: number, acct: any) => n + (acct?.campaigns?.length ?? 0), 0)

  // Ad connections have to be in before the verdict: settling without them
  // reports `no_data` for an account that does have campaigns.
  const isSettled =
    !isLoading &&
    !isLoadingRevenue &&
    !isLoadingRevenueMetrics &&
    !isLoadingMeta &&
    !isLoadingGoogleAds
  useEffect(() => {
    if (!propertyId || !isSettled) return

    logAttributionEvent(
      hasAdData || hasRevenue ? 'dashboard_loaded' : 'no_data',
      { propertyId },
      { period, rowCount: adCampaignCount },
      { dedupKey: `settled:${propertyId}:${period}` },
    )
    // Once per property/period settle — campaign counts shift on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, period, isSettled])

  return {
    metaConnections,
    googleAdsConnections,
    isRevenueConfigured,
    connectorType,
    isLoadingRevenue,
    adTotals,
    adSpendByPlatform,
    totalRevenueDollars,
    attributedRevenueDollars,
    adRevenueDollars,
    adPaymentCount,
    trueROAS,
    totalTrialCount,
    revenueAll,
    classified,
    revenueDims,
    humblyticsData,
    landingPagesWithRevenue,
    chartData,
    revenueMetrics,
    isLoadingMeta,
    isLoadingGoogleAds,
    isLoadingRevenueMetrics,
    metaError,
    dateRange,
    paymentCount,
    customerCount,
    trialCountAll: freeTrialRows.length,
    sourceRevenueRows,
    topTrialSource: trialSource,
    hasAdData,
    hasRevenue,
    adsAttributionData,
    isLoadingAdsAttribution,
    adsAttributionError,
  }
}
